import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type { HasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { COLOR_PARITY_LOSS } from 'frontend-utils/utils/css-variable';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { ParityInsightSegmentationItem } from 'lumen/models';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import renderTooltip from 'lumen/utils/charts/render-tooltip';
import type { ChartCustomPoint } from 'lumen/utils/constants/highcharts-constants';

import styles from './chart-issue-type.module.css';

const ALLOWED_ISSUE_TYPES = [
  'LOSS_SOLDOUT',
  'LOSS_ROOMTYPE',
  'LOSS_PRICE_DIFFERENCE',
] as const;

type AllowedIssueType = (typeof ALLOWED_ISSUE_TYPES)[number];

const ISSUE_TYPE_TRANSLATIONS: Record<AllowedIssueType, string> = {
  LOSS_SOLDOUT: tKey('parity_analytics.issue_type.loss_soldout'),
  LOSS_ROOMTYPE: tKey('parity_analytics.issue_type.loss_roomtype'),
  LOSS_PRICE_DIFFERENCE: tKey(
    'parity_analytics.issue_type.loss_price_difference',
  ),
};

type IssueTypeItem = ParityInsightSegmentationItem & {
  segmentationValueId: AllowedIssueType;
};

function isAllowedIssueType(value: string): value is AllowedIssueType {
  return ALLOWED_ISSUE_TYPES.some((issueType) => issueType === value);
}

function isIssueTypeItem(
  item: ParityInsightSegmentationItem,
): item is IssueTypeItem {
  return isAllowedIssueType(item.segmentationValueId);
}

interface IssueTypePoint extends ChartCustomPoint {
  issue: IssueTypeItem;
}

interface IssueTypeTooltipContext
  extends Highcharts.TooltipFormatterContextObject {
  point: IssueTypePoint;
}

type IssueTypeSeries = Omit<Highcharts.SeriesBarOptions, 'data'> & {
  data: { issue: IssueTypeItem; x: number; y: number }[];
};

interface ParityChartsChartIssueTypeSignature {
  Args: {
    hideHotelsMetric: boolean;
    issueTypeData: HasMany<ParityInsightSegmentationItem> | undefined;
    parsedSegmentationFilters?: Record<string, string[]>;
    segmentationFilterClicked?: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    showLoadingPlaceholderState: boolean;
    totalLoss?: number;
  };
  Element: HTMLElement;
}

export default class ParityChartsChartIssueType extends Component<ParityChartsChartIssueTypeSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  @service declare usageAnalytics: Services['usage-analytics'];

  trackHoverTask = task({ restartable: true }, async (issueType: string) => {
    await timeout(TOOLTIP_HOVER_TIMEOUT);
    this.usageAnalytics.track('Issue Type Details Viewed', {
      eventTrigger: 'issue-type-chart',
      issueType,
    });
  });

  get chartData(): IssueTypeSeries[] {
    if (this.args.issueTypeData) {
      const allIssues = this.args.issueTypeData
        .filter(isIssueTypeItem)
        .sort((a, b) => b.calculatedValues.loss - a.calculatedValues.loss);
      const series: IssueTypeSeries[] = [];

      if (allIssues) {
        allIssues.forEach((issue, index) => {
          series.push({
            type: 'bar',
            name: issue.segmentationValueId,
            data: [
              {
                x: index,
                y: issue.calculatedValues.loss,
                issue,
              },
            ],
            color: `var(${COLOR_PARITY_LOSS})`,
            borderColor: 'var(--prism-color-border-inverse-emphasis)',
          });
        });

        return series;
      }
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        zoomType: null,
        type: 'bar',
        title: '',
        height: null, // Grow chart to fill container
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
      },

      title: {
        text: 'parity-issuetype-chart',
      },

      legend: {
        enabled: false,
      },

      tooltip: {
        shared: false,
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shape: 'square',
        shadow: false,
        outside: true,
        distance: 20,

        formatter(this: IssueTypeTooltipContext): string {
          const { issue } = this.point;

          return renderTooltip(ParityTooltipsTooltipKpi, {
            owner: getOwner(that),
            args: {
              data: {
                totalNbOfShops: issue.value.loss,
                totalNbOfHotels: issue.value.subscriptions,
                singleValue: issue.calculatedValues.loss,
              },
              displayName: that.intl.t(
                ISSUE_TYPE_TRANSLATIONS[issue.segmentationValueId],
              ),
              hideShoppedHotels: that.args.hideHotelsMetric,
            },
          });
        },
        useHTML: true,
      },

      xAxis: {
        minPadding: 0.5,
        maxPadding: 0.5,
        tickInterval: 1,
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            const issueType = this.chart.series[Number(this.value)]?.name;

            if (issueType && isAllowedIssueType(issueType)) {
              return that.intl.t(ISSUE_TYPE_TRANSLATIONS[issueType]);
            }

            return '';
          },
        },
      },

      yAxis: {
        min: 0,
        title: {
          enabled: false,
        },
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            // @ts-expect-error: Argument of type 'string | number' is not assignable to parameter of type 'number | bigint | StringNumericLiteral | null | undefined'.
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },

      plotOptions: {
        series: {
          pointPadding: 0.1,
          groupPadding: 0.1,
          pointWidth: null,
          stacking: 'normal',
        },
        bar: {
          point: {
            events: {
              mouseOver(this: IssueTypePoint): void {
                void that.trackHoverTask.perform(
                  this.issue.segmentationValueId,
                );
              },
              mouseOut(): void {
                void that.trackHoverTask.cancelAll();
              },
            },
          },
        },
      },
    };
  }

  get knownIssueTypes(): string[] {
    if (this.args.issueTypeData) {
      return this.args.issueTypeData.map((issue) => issue.segmentationValueId);
    }

    return [];
  }

  <template>
    <PrismCard ...attributes>
      <:header as |Header|>
        <div class="flex items-center">
          {{#if @showLoadingPlaceholderState}}
            <p class="loading-pulse--col-3"></p>
          {{else}}
            <Header.title>
              {{t "parity_analytics.issue_type"}}
            </Header.title>
          {{/if}}
        </div>
      </:header>

      <:content>
        <div class={{styles.breakdown-content}}>
          <GeneralChartsOtaChart
            {{! @glint-expect-error: Type '{ chart: { zoomType: null; type: string; title: string; height: null; ... }; ... }' is not assignable to type 'Options'. }}
            @chartOptions={{this.chartOptions}}
            @model={{this.chartData}}
            class="h-full"
          />
        </div>
      </:content>
    </PrismCard>
  </template>
}
